import { useState, useEffect } from 'react';
import { supabase, Agency, ScreeningQuestion } from '../lib/supabase';
import { Upload, Send, Check, AlertCircle, FileText, X, Lock, ArrowLeft } from 'lucide-react';

type Props = {
  slug: string;
};

export default function ApplyPage({ slug }: Props) {
  const [agency, setAgency] = useState<Agency | null>(null);
  const [questions, setQuestions] = useState<ScreeningQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inactive, setInactive] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAgency();
  }, [slug]);

  async function loadAgency() {
    setLoading(true);
    const { data: agencyData } = await supabase
      .from('agencies')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (!agencyData) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setAgency(agencyData);

    // Check if subscription grants access — block if canceled past end date
    // or suspended
    const status = agencyData.subscription_status;
    const endsAt = agencyData.subscription_ends_at;
    const pastEnd = endsAt ? new Date(endsAt) < new Date() : true;
    const accessBlocked =
      (status === 'canceled' && pastEnd) ||
      status === 'suspended';
    if (accessBlocked) {
      setInactive(true);
      setLoading(false);
      return;
    }

    const { data: qs } = await supabase
      .from('screening_questions')
      .select('*')
      .eq('agency_id', agencyData.id)
      .order('position');
    setQuestions(qs ?? []);
    setLoading(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!agency) return;
    setError('');

    // Validate required questions
    for (const q of questions) {
      if (q.required && !answers[q.id]?.trim()) {
        setError(`Please answer: "${q.label}"`);
        return;
      }
    }

    setSubmitting(true);

    try {
      // Upload CV if provided
      let cvUrl: string | null = null;
      if (cvFile) {
        const ext = cvFile.name.split('.').pop()?.toLowerCase() ?? 'pdf';
        const safeName = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'candidate';
        const safeEmail = email.trim().toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-+|-+$/g, '');
        const path = `${agency.id}/${safeName}-${safeEmail}-${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from('cvs')
          .upload(path, cvFile, {
            contentType: cvFile.type || undefined,
            upsert: false,
          });
        if (uploadErr) {
          setError(`CV upload failed: ${uploadErr.message}`);
          setSubmitting(false);
          return;
        }
        // Store the storage path (not a public URL) — the dashboard will
        // generate a signed URL to securely download the CV
        cvUrl = path;
      }

      // Insert application
      const { data: app, error: appErr } = await supabase
        .from('applications')
        .insert({
          agency_id: agency.id,
          candidate_name: name.trim(),
          candidate_email: email.trim(),
          candidate_phone: phone.trim() || null,
          cv_url: cvUrl,
          status: 'new',
        })
        .select()
        .single();

      if (appErr) throw appErr;

      // Insert answers
      if (questions.length > 0) {
        const answerRows = questions
          .filter((q) => answers[q.id] !== undefined)
          .map((q) => ({
            application_id: app.id,
            question_id: q.id,
            question_label: q.label,
            answer: answers[q.id] ?? null,
          }));
        if (answerRows.length > 0) {
          const { error: ansErr } = await supabase.from('application_answers').insert(answerRows);
          if (ansErr) throw ansErr;
        }
      }

      // Trigger notification (fire-and-forget, don't block the user)
      fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-application`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ applicationId: app.id }),
        }
      ).catch(() => {});

      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-gray-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Page not found</h1>
          <p className="text-gray-500 text-sm">This application link doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  if (inactive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Applications paused</h1>
          <p className="text-gray-500 text-sm mb-1">
            <strong>{agency?.name}</strong> is not accepting applications right now.
          </p>
          <p className="text-gray-400 text-sm">Please check back later or contact the agency directly.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Application received!</h1>
          <p className="text-gray-500 mb-1">
            Thank you, <strong>{name}</strong>. Your application to{' '}
            <strong>{agency?.name}</strong> has been submitted.
          </p>
          <p className="text-gray-400 text-sm">We'll be in touch at {email}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-9 h-9 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="font-bold text-gray-900">{agency?.name}</p>
            <p className="text-xs text-gray-400">Job Application</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 pb-16">
        {/* Hero text */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Apply to <span className="text-blue-600">{agency?.name}</span>
          </h1>
          <p className="text-gray-500 text-sm">
            Fill in your details below. It takes less than 5 minutes.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Personal info */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your Details</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                required
                className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@email.com"
                  required
                  className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* CV upload */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">CV / Resume</p>
            {cvFile ? (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-blue-700 flex-1 truncate">{cvFile.name}</span>
                <button
                  type="button"
                  onClick={() => setCvFile(null)}
                  className="text-blue-400 hover:text-blue-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed border-gray-200 rounded-md cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-all">
                <Upload className="w-7 h-7 text-gray-300" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Click to upload your CV</p>
                  <p className="text-xs text-gray-400 mt-0.5">PDF, DOC or DOCX — max 10MB</p>
                </div>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size <= 10 * 1024 * 1024) setCvFile(f);
                    else if (f) setError('File too large. Maximum 10MB.');
                  }}
                />
              </label>
            )}
          </div>

          {/* Screening questions */}
          {questions.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Screening Questions</p>
              {questions.map((q) => (
                <QuestionField
                  key={q.id}
                  question={q}
                  value={answers[q.id] ?? ''}
                  onChange={(val) => setAnswers((prev) => ({ ...prev, [q.id]: val }))}
                />
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2.5 p-4 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-sm rounded-lg transition-colors shadow-lg shadow-blue-600/20"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Application
              </>
            )}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by <span className="font-semibold">CareHire</span> — Secure, GDPR-compliant recruitment
        </p>
      </div>
    </div>
  );
}

function QuestionField({
  question, value, onChange,
}: {
  question: ScreeningQuestion;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-800 mb-2">
        {question.label}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {question.type === 'yes_no' && (
        <div className="flex gap-3">
          {['Yes', 'No'].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`flex-1 py-2.5 rounded-md border text-sm font-semibold transition-all ${
                value === opt
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600 bg-white'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {question.type === 'multiple_choice' && (
        <div className="grid gap-2">
          {(question.options ?? []).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`flex items-center gap-3 px-4 py-3 rounded-md border text-sm text-left transition-all ${
                value === opt
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 text-gray-700 hover:border-blue-300 bg-white'
              }`}
            >
              <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                value === opt ? 'border-white bg-white' : 'border-gray-300'
              }`}>
                {value === opt && <span className="block w-2 h-2 bg-blue-600 rounded-full m-0.5" />}
              </span>
              {opt}
            </button>
          ))}
        </div>
      )}

      {question.type === 'short_text' && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Your answer..."
        />
      )}

      {question.type === 'long_text' && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
          rows={4}
          className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Your answer..."
        />
      )}
    </div>
  );
}
